import type { TicketProgress } from '@shared/schemas';
import { ticketAgeInColumnDays } from '@/lib/ticket-meta';

/**
 * Q-4 stale-detection chip · «N дн в колонке» visual indicator на Board cards.
 *
 * Renders nothing если no timestamp recordable (defensive · brand-new tickets
 * don't show age). Stale tier visually distinct (≥ `staleThresholdDays` days):
 *   - tier 0 (< stale): muted neutral chip
 *   - tier 1 (≥ stale): warning-tinted (yellow/amber) flag
 *   - tier 2 (≥ 2× stale): danger-tinted (red) call-to-action
 *
 * Helper `ticketAgeInColumnDays` @ cfa3c1a handles columnChangedAt → startedAt
 * fallback + clock-skew defense.
 */
export interface TicketAgeChipProps {
  ticket: Pick<TicketProgress, 'columnChangedAt' | 'startedAt'> | undefined;
  /** Days threshold для tier 1 «stale» warning · default 3. */
  staleThresholdDays?: number;
  /** Override «now» reference. */
  now?: Date;
}

export function TicketAgeChip({
  ticket,
  staleThresholdDays = 3,
  now,
}: TicketAgeChipProps) {
  const days = ticketAgeInColumnDays(ticket, now);
  if (days === null) return null;

  const tier = days >= staleThresholdDays * 2 ? 2 : days >= staleThresholdDays ? 1 : 0;
  const label = formatAgeLabel(days);

  const colors = tier === 2
    ? { bg: 'var(--surface-danger, #ffe0e0)', ink: 'var(--ink-danger, #8c0000)', border: 'var(--border-danger, #ffadad)' }
    : tier === 1
    ? { bg: 'var(--surface-warning, #fff4e0)', ink: 'var(--ink-warning, #8c4a00)', border: 'var(--border-warning, #ffd29a)' }
    : { bg: 'var(--surface-2)', ink: 'var(--ink-muted)', border: 'var(--border-strong)' };

  return (
    <span
      className={`ticket-age-chip ticket-age-chip-tier-${tier}`}
      title={`${days} дн в текущей колонке${tier > 0 ? ' · возможно застряла' : ''}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        fontSize: 'var(--font-xs, 12px)',
        borderRadius: 12,
        background: colors.bg,
        color: colors.ink,
        border: `1px solid ${colors.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {tier > 0 && <AgeChipIcon tier={tier} />}
      {label}
    </span>
  );
}

/**
 * Hand-drawn stale marker · replaces 🚨/⏰ emoji (no-emoji rule · Genie 6 JUN).
 * Inherits chip ink via `currentColor` — корректно во всех темах (light/dark/тёплая).
 * tier 2 = треугольник-внимание («застряла») · tier 1 = часы («давно в колонке»).
 */
function AgeChipIcon({ tier }: { tier: number }) {
  if (tier >= 2) {
    return (
      <svg
        className="ticket-age-chip__icon"
        data-tier="2"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ flexShrink: 0 }}
      >
        <path d="M12 3.6 2.4 20.4h19.2L12 3.6Z" />
        <path d="M12 10v4.4" />
        <path d="M12 17.6h.01" />
      </svg>
    );
  }
  return (
    <svg
      className="ticket-age-chip__icon"
      data-tier="1"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="8.4" />
      <path d="M12 7.3v5l3.2 2" />
    </svg>
  );
}

/** Format days count к Russian relative label: «сегодня» / «1 дн» / «N дн». */
function formatAgeLabel(days: number): string {
  if (days === 0) return 'сегодня';
  if (days === 1) return '1 дн';
  return `${days} дн`;
}
